import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchLabelPreviewHtml,
  fetchReceiptPreviewHtml,
  listPrintPrinters,
  printTestLabel,
  printTestReceipt,
} from '@/lib/api/print.js';
import { getCurrentStore, updateCurrentStore } from '@/lib/api/stores.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { LABEL_SIZE_PRESET_OPTIONS, type LabelSizePresetId } from '@fixflow/validation';

const defaultLabel = {
  showBarcode: true,
  showTech: true,
  showPromisedDate: true,
  showPhone: true,
  copies: 1,
  labelSizePreset: '2.25x4' as LabelSizePresetId,
  labelWidthIn: 2.25,
  labelHeightIn: 4,
};

const defaultReceipt = {
  showLogo: true,
  showPortalLink: true,
  thankYouMessage: 'Thank you for your business!',
  copies: 1,
};

export function StorePrintingSettings() {
  const queryClient = useQueryClient();
  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const store = storeQuery.data;

  const [labelPrinter, setLabelPrinter] = useState('');
  const [receiptPrinter, setReceiptPrinter] = useState('');
  const [labelCfg, setLabelCfg] = useState(defaultLabel);
  const [receiptCfg, setReceiptCfg] = useState(defaultReceipt);
  const [previewTicketId, setPreviewTicketId] = useState('');

  useEffect(() => {
    if (!store) return;
    setLabelPrinter(store.printnodeLabelPrinterId != null ? String(store.printnodeLabelPrinterId) : '');
    setReceiptPrinter(store.printnodeReceiptPrinterId != null ? String(store.printnodeReceiptPrinterId) : '');
    const lc = store.labelConfig;
    if (lc && typeof lc === 'object') {
      setLabelCfg({
        showBarcode: lc.showBarcode !== false,
        showTech: lc.showTech !== false,
        showPromisedDate: lc.showPromisedDate !== false,
        showPhone: lc.showPhone !== false,
        copies:
          typeof lc.copies === 'number' && lc.copies >= 1 ? lc.copies : defaultLabel.copies,
        labelSizePreset:
          typeof lc.labelSizePreset === 'string' && LABEL_SIZE_PRESET_OPTIONS.some((o) => o.id === lc.labelSizePreset)
            ? (lc.labelSizePreset as LabelSizePresetId)
            : defaultLabel.labelSizePreset,
        labelWidthIn:
          typeof lc.labelWidthIn === 'number' && Number.isFinite(lc.labelWidthIn)
            ? lc.labelWidthIn
            : defaultLabel.labelWidthIn,
        labelHeightIn:
          typeof lc.labelHeightIn === 'number' && Number.isFinite(lc.labelHeightIn)
            ? lc.labelHeightIn
            : defaultLabel.labelHeightIn,
      });
    }
    const rc = store.receiptConfig;
    if (rc && typeof rc === 'object') {
      setReceiptCfg({
        showLogo: rc.showLogo !== false,
        showPortalLink: rc.showPortalLink !== false,
        thankYouMessage:
          typeof rc.thankYouMessage === 'string' ? rc.thankYouMessage : defaultReceipt.thankYouMessage,
        copies:
          typeof rc.copies === 'number' && rc.copies >= 1 ? rc.copies : defaultReceipt.copies,
      });
    }
  }, [store]);

  const printersQuery = useQuery({
    queryKey: ['print-printers'],
    queryFn: listPrintPrinters,
    enabled: false,
  });

  const savePrintersMut = useMutation({
    mutationFn: () =>
      updateCurrentStore({
        printnodeLabelPrinterId: labelPrinter ? Number(labelPrinter) : null,
        printnodeReceiptPrinterId: receiptPrinter ? Number(receiptPrinter) : null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      toastSuccess('Printer selection saved');
    },
    onError: (e) => toastError(parseApiError(e).message, parseApiError(e).code),
  });

  const saveLabelCfgMut = useMutation({
    mutationFn: () => updateCurrentStore({ labelConfig: labelCfg }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      toastSuccess('Label options saved');
    },
    onError: (e) => toastError(parseApiError(e).message, parseApiError(e).code),
  });

  const saveReceiptCfgMut = useMutation({
    mutationFn: () => updateCurrentStore({ receiptConfig: receiptCfg }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      toastSuccess('Receipt options saved');
    },
    onError: (e) => toastError(parseApiError(e).message, parseApiError(e).code),
  });

  const testLabelMut = useMutation({
    mutationFn: () => printTestLabel({ copies: labelCfg.copies }),
    onSuccess: () => toastSuccess('Test label sent to printer'),
    onError: (e) => toastError(parseApiError(e).message, parseApiError(e).code),
  });

  const testReceiptMut = useMutation({
    mutationFn: () => printTestReceipt({ copies: receiptCfg.copies }),
    onSuccess: () => toastSuccess('Test receipt sent to printer'),
    onError: (e) => toastError(parseApiError(e).message, parseApiError(e).code),
  });

  async function previewLabel() {
    const id = previewTicketId.trim();
    if (!id) {
      toastError('Enter a ticket UUID to preview.');
      return;
    }
    try {
      const html = await fetchLabelPreviewHtml(id);
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(html);
        w.document.close();
      }
    } catch (e) {
      toastError(parseApiError(e).message, parseApiError(e).code);
    }
  }

  async function previewReceipt() {
    const id = previewTicketId.trim();
    if (!id) {
      toastError('Enter a ticket UUID to preview.');
      return;
    }
    try {
      const html = await fetchReceiptPreviewHtml(id);
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(html);
        w.document.close();
      }
    } catch (e) {
      toastError(parseApiError(e).message, parseApiError(e).code);
    }
  }

  const printers = printersQuery.data ?? [];

  return (
    <section className="mt-8 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-lg font-semibold text-slate-950">Printing (PrintNode)</h3>
      <p className="mt-1 text-sm text-slate-600">
        Install the PrintNode client on the store PC and add{' '}
        <code className="rounded bg-slate-100 px-1">PRINTNODE_API_KEY</code> to the API server environment. Printers
        appear here after the client registers them.
      </p>

      <div className="mt-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            disabled={printersQuery.isFetching}
            onClick={() => void printersQuery.refetch()}
          >
            {printersQuery.isFetching ? 'Loading…' : 'Refresh printers'}
          </button>
          {printersQuery.isError ? (
            <span className="text-sm text-rose-700">
              {parseApiError(printersQuery.error).message} — check the API key and PrintNode account.
            </span>
          ) : null}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="block text-sm">
            <span className="font-medium text-slate-800">Label printer (Dymo / etc.)</span>
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={labelPrinter}
              onChange={(e) => setLabelPrinter(e.target.value)}
            >
              <option value="">Not configured</option>
              {printers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.computerName || 'PC'})
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-800">Receipt printer (Star / etc.)</span>
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={receiptPrinter}
              onChange={(e) => setReceiptPrinter(e.target.value)}
            >
              <option value="">Not configured</option>
              {printers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.computerName || 'PC'})
                </option>
              ))}
            </select>
          </label>
        </div>

        <button
          type="button"
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          disabled={savePrintersMut.isPending}
          onClick={() => savePrintersMut.mutate()}
        >
          Save printer settings
        </button>
      </div>

      <div className="mt-8 border-t border-slate-200 pt-6">
        <h4 className="font-semibold text-slate-950">Label layout</h4>

        <label className="mt-3 block text-sm">
          <span className="font-medium text-slate-800">Label size</span>
          <select
            className="mt-1 w-full max-w-xl rounded-xl border border-slate-300 px-3 py-2"
            value={labelCfg.labelSizePreset}
            onChange={(e) =>
              setLabelCfg((c) => ({
                ...c,
                labelSizePreset: e.target.value as LabelSizePresetId,
              }))
            }
          >
            {LABEL_SIZE_PRESET_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-slate-500">
            PDF page size follows this selection. Save label options before test printing.
          </span>
        </label>

        {labelCfg.labelSizePreset === 'custom' ? (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-medium text-slate-800">
              Width (inches)
              <input
                type="number"
                min={0.5}
                max={6}
                step="0.015625"
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 font-mono text-sm"
                value={labelCfg.labelWidthIn}
                onChange={(e) =>
                  setLabelCfg((c) => ({
                    ...c,
                    labelWidthIn: Math.min(6, Math.max(0.5, Number(e.target.value) || 0.5)),
                  }))
                }
              />
            </label>
            <label className="block text-sm font-medium text-slate-800">
              Height (inches)
              <input
                type="number"
                min={0.5}
                max={12}
                step="0.015625"
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 font-mono text-sm"
                value={labelCfg.labelHeightIn}
                onChange={(e) =>
                  setLabelCfg((c) => ({
                    ...c,
                    labelHeightIn: Math.min(12, Math.max(0.5, Number(e.target.value) || 0.5)),
                  }))
                }
              />
            </label>
          </div>
        ) : null}

        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {(
            [
              ['showBarcode', 'Show barcode'],
              ['showTech', 'Show technician'],
              ['showPromisedDate', 'Show promised date'],
              ['showPhone', 'Show customer phone'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
              <span>{label}</span>
              <input
                type="checkbox"
                checked={labelCfg[key]}
                onChange={(e) => setLabelCfg((c) => ({ ...c, [key]: e.target.checked }))}
              />
            </label>
          ))}
          <label className="block md:col-span-2">
            <span className="text-sm font-medium">Copies per print</span>
            <input
              type="number"
              min={1}
              max={99}
              className="mt-1 w-32 rounded-xl border border-slate-300 px-3 py-2"
              value={labelCfg.copies}
              onChange={(e) =>
                setLabelCfg((c) => ({ ...c, copies: Math.max(1, Number(e.target.value) || 1) }))
              }
            />
          </label>
        </div>
        <button
          type="button"
          className="mt-3 rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800"
          disabled={saveLabelCfgMut.isPending}
          onClick={() => saveLabelCfgMut.mutate()}
        >
          Save label options
        </button>
      </div>

      <div className="mt-8 border-t border-slate-200 pt-6">
        <h4 className="font-semibold text-slate-950">Receipt layout</h4>
        <div className="mt-3 space-y-3">
          {(
            [
              ['showLogo', 'Show logo (when configured)'],
              ['showPortalLink', 'Show portal tracking link'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
              <span>{label}</span>
              <input
                type="checkbox"
                checked={receiptCfg[key]}
                onChange={(e) => setReceiptCfg((c) => ({ ...c, [key]: e.target.checked }))}
              />
            </label>
          ))}
          <label className="block">
            <span className="text-sm font-medium">Thank you message</span>
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={receiptCfg.thankYouMessage}
              onChange={(e) => setReceiptCfg((c) => ({ ...c, thankYouMessage: e.target.value }))}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Copies per print</span>
            <input
              type="number"
              min={1}
              max={99}
              className="mt-1 w-32 rounded-xl border border-slate-300 px-3 py-2"
              value={receiptCfg.copies}
              onChange={(e) =>
                setReceiptCfg((c) => ({ ...c, copies: Math.max(1, Number(e.target.value) || 1) }))
              }
            />
          </label>
        </div>
        <button
          type="button"
          className="mt-3 rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800"
          disabled={saveReceiptCfgMut.isPending}
          onClick={() => saveReceiptCfgMut.mutate()}
        >
          Save receipt options
        </button>
      </div>

      <div className="mt-8 border-t border-slate-200 pt-6">
        <h4 className="font-semibold text-slate-950">Preview & test</h4>
        <p className="mt-1 text-sm text-slate-600">
          Enter a ticket UUID from your queue to open an HTML preview in a new tab.
        </p>
        <label className="mt-3 block">
          <span className="text-sm font-medium text-slate-800">Ticket ID</span>
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 font-mono text-sm"
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            value={previewTicketId}
            onChange={(e) => setPreviewTicketId(e.target.value)}
          />
        </label>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800"
            onClick={() => void previewLabel()}
          >
            Preview label
          </button>
          <button
            type="button"
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800"
            onClick={() => void previewReceipt()}
          >
            Preview receipt
          </button>
          <button
            type="button"
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800"
            disabled={testLabelMut.isPending}
            onClick={() => testLabelMut.mutate()}
          >
            Test label print
          </button>
          <button
            type="button"
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800"
            disabled={testReceiptMut.isPending}
            onClick={() => testReceiptMut.mutate()}
          >
            Test receipt print
          </button>
        </div>
      </div>
    </section>
  );
}
